import { memo, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  Box,
  Checkbox,
  Typography,
} from "@mui/material";

import { CustomAutocomplete } from "../../../components/Inputs/CustomAutocomplete";
import { isSelectAllRow } from "../../../hooks/useSelectAllOptions";
import { useQuery } from "react-query";
import { simpleQuery } from "../../../services/utils";

type OptionType = { id: number; label: string };

const renderOptions = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: OptionType,
  state: AutocompleteRenderOptionState
) => (
  <li {...props} key={option.id}>
    <Checkbox sx={{ mr: 1, p: 0 }} checked={state.selected} />
    {option.label}
  </li>
);

const getOptionLabel = (option: OptionType) => option.label;

const isOptionEqualToValue = (option: OptionType, value: OptionType) =>
  option.id === value.id;

const CustomerSelector = memo(
  ({
    error,
    defaultValue,
    limitTags,
    onCustomerChange,
  }: {
    error?: string;
    defaultValue?: number[] | "all";
    limitTags: number;
    onCustomerChange: (customers: number[] | "all") => void;
  }) => {
    const intl = useIntl();
    const [selectorValue, setSelectorValue] = useState<OptionType[]>([]);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchText), 500);
      return () => clearTimeout(timer);
    }, [searchText]);

    const customerListQuery = useQuery(
      ["banner-customers", debouncedSearch],
      () => {
        const params = new URLSearchParams({ page_size: "100" });
        if (debouncedSearch) params.set("search", debouncedSearch);
        return simpleQuery(`/rbl/customers/?${params}`, { method: "GET" });
      },
      {
        select: (data): OptionType[] =>
          data.results.map((customer: { id: number; name: string }) => ({
            id: customer.id,
            label: customer.name.replace("-revbell", ""),
          })),
      }
    );

    const handleChange = useCallback(
      (
        _: React.SyntheticEvent,
        customers: OptionType[],
        _reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<OptionType>
      ) => {
        setSelectorValue(customers);

        // "all" only comes from an explicit "select all" on the full list: a
        // search only loads a subset, and ticking every loaded customer by
        // hand stays an explicit id list
        const allSelected =
          !debouncedSearch &&
          customers.length > 0 &&
          isSelectAllRow(details?.option);

        onCustomerChange(
          allSelected ? "all" : customers.map((customer) => customer.id)
        );
      },
      [debouncedSearch, onCustomerChange]
    );

    // If defautltValue exist, fill selectorValue
    // Only run against the full list (no active search) — re-mapping against filtered data
    // would trigger a MUI "reset" that clears the search input.
    useEffect(() => {
      if (customerListQuery.data && defaultValue && !debouncedSearch) {
        if (typeof defaultValue === "string") {
          setSelectorValue(customerListQuery.data);
        } else {
          const options = defaultValue
            .map((customerId) =>
              customerListQuery.data.find(
                (customer) => customer.id === customerId
              )
            )
            .filter((option) => !!option);

          setSelectorValue(options);
        }
      }
    }, [defaultValue, customerListQuery.data, debouncedSearch]);

    return (
      <Box
        sx={{
          marginTop: 1,
          "& .MuiInputBase-root.MuiOutlinedInput-root": { borderRadius: "4px" },
        }}
      >
        <CustomAutocomplete
          disableCloseOnSelect={true}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          label={intl.formatMessage({ id: "BANNER_CUSTOMER_SELECTOR" })}
          loading={customerListQuery.isLoading}
          limitTags={limitTags}
          // Server-side search: every loaded option is a match for "select the filtered results"
          filterOptions={(x) => x}
          multiple
          onChange={handleChange}
          onInputChange={(_, val, reason) => {
            if (reason === "input" || reason === "clear" || reason === "blur")
              setSearchText(val);
          }}
          options={customerListQuery.data ?? []}
          renderOptions={renderOptions}
          tagsLabelKey={"label"}
          value={selectorValue}
        />
        {error && (
          <Typography
            sx={{
              color: "error.main",
              fontSize: "0.75rem",
              ml: 1.75,
            }}
          >
            <FormattedMessage id={error} />
          </Typography>
        )}
      </Box>
    );
  }
);

export default CustomerSelector;
